(() => {
  const LENGTH_RE = /(\d+(?:\.\d+)?)\s*mm\b/i
  const APPLY_RETRY_MS = 40
  const APPLY_MAX_ATTEMPTS = 12

  /** @type {Map<string, number>} */
  const pendingApplyTimers = new Map()
  /** @type {Set<string>} */
  const userLockedKeys = new Set()

  function extractLength(text) {
    if (!text) return null
    const match = String(text).match(LENGTH_RE)
    return match ? match[1] : null
  }

  function getSelectedLength(variantSelect) {
    const opt = variantSelect.querySelector('option:checked')
    return extractLength(opt && opt.textContent)
  }

  function findOptionValueForLength(variantSelect, length) {
    if (!length) return null
    const options = Array.from(variantSelect.options || [])
    const match = options.find((o) => extractLength(o.textContent) === length && !o.disabled && o.value)
    return match ? match.value : null
  }

  function storeLength(formEl, lashId, length) {
    if (!formEl || !lashId || !length) return
    formEl.dataset[`deluxeLen${lashId}`] = length
  }

  function readStoredLength(formEl, lashId) {
    if (!formEl || !lashId) return null
    return formEl.dataset[`deluxeLen${lashId}`] || null
  }

  function lockKey(formEl, lashNum) {
    return `${formEl?.dataset?.productid || formEl?.id || 'dsk'}:${lashNum}`
  }

  function clearPendingApply(key) {
    const timer = pendingApplyTimers.get(key)
    if (timer) {
      window.clearTimeout(timer)
      pendingApplyTimers.delete(key)
    }
  }

  /**
   * Apply preserved mm once after product change.
   * Do NOT poll for seconds — that blocks the size dropdown from working.
   */
  function applyPreservedLengthOnce({ formEl, lashNum, attempt = 0 }) {
    const key = lockKey(formEl, lashNum)
    if (userLockedKeys.has(key)) return

    const preserved = readStoredLength(formEl, lashNum)
    if (!preserved) return

    const variantSelect = formEl.querySelector(`#lash${lashNum}variants`)
    if (!(variantSelect instanceof HTMLSelectElement)) {
      if (attempt < APPLY_MAX_ATTEMPTS) {
        const timer = window.setTimeout(() => {
          applyPreservedLengthOnce({ formEl, lashNum, attempt: attempt + 1 })
        }, APPLY_RETRY_MS)
        pendingApplyTimers.set(key, timer)
      }
      return
    }

    // Wait until options are rebuilt for the new product.
    const realOptions = Array.from(variantSelect.options || []).filter((o) => o.value && !o.disabled)
    if (!realOptions.length) {
      if (attempt < APPLY_MAX_ATTEMPTS) {
        const timer = window.setTimeout(() => {
          applyPreservedLengthOnce({ formEl, lashNum, attempt: attempt + 1 })
        }, APPLY_RETRY_MS)
        pendingApplyTimers.set(key, timer)
      }
      return
    }

    const valueToSelect = findOptionValueForLength(variantSelect, preserved)
    if (!valueToSelect) return

    if (variantSelect.value !== valueToSelect) {
      variantSelect.value = valueToSelect
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  function handleVariantChange(e) {
    const select = e.target
    if (!(select instanceof HTMLSelectElement)) return
    const id = select.id || ''
    const m = id.match(/^lash(\d+)variants$/)
    if (!m) return

    const lashNum = m[1]
    const formEl = select.closest('[data-component="product/deluxe-form"]')
    if (!formEl) return

    const key = lockKey(formEl, lashNum)
    // User chose a size — stop any pending re-apply from a product switch.
    userLockedKeys.add(key)
    clearPendingApply(key)
    window.setTimeout(() => userLockedKeys.delete(key), 300)

    const length = getSelectedLength(select)
    if (length) storeLength(formEl, lashNum, length)
  }

  function handleProductChange(e) {
    const select = e.target
    if (!(select instanceof HTMLSelectElement)) return
    const id = select.id || ''
    const m = id.match(/^lash(\d+)$/)
    if (!m) return

    const lashNum = m[1]
    const formEl = select.closest('[data-component="product/deluxe-form"]')
    if (!formEl) return

    const key = lockKey(formEl, lashNum)
    userLockedKeys.delete(key)
    clearPendingApply(key)

    // Theme JS rebuilds size options asynchronously — apply preserved mm a few times briefly.
    window.requestAnimationFrame(() => {
      applyPreservedLengthOnce({ formEl, lashNum, attempt: 0 })
    })
  }

  function handleVariantPointerDown(e) {
    const select = e.target
    if (!(select instanceof HTMLSelectElement)) return
    const id = select.id || ''
    const m = id.match(/^lash(\d+)variants$/)
    if (!m) return

    const formEl = select.closest('[data-component="product/deluxe-form"]')
    if (!formEl) return

    const key = lockKey(formEl, m[1])
    // Opening / interacting with size select cancels preserve retries.
    userLockedKeys.add(key)
    clearPendingApply(key)
  }

  function initExisting() {
    document.querySelectorAll('[data-component="product/deluxe-form"]').forEach((formEl) => {
      for (let i = 1; i <= 3; i++) {
        const variantSelect = formEl.querySelector(`#lash${i}variants`)
        if (variantSelect instanceof HTMLSelectElement) {
          const len = getSelectedLength(variantSelect)
          if (len) storeLength(formEl, String(i), len)
        }
      }
    })
  }

  document.addEventListener('change', handleVariantChange)
  document.addEventListener('change', handleProductChange)
  document.addEventListener('pointerdown', handleVariantPointerDown, true)

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExisting, { once: true })
  } else {
    initExisting()
  }

  window.setTimeout(initExisting, 800)
})()
